import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  createProfilFastener,
  deleteProfilFastener,
  getProfilFastener,
  listProfilFasteners,
  updateProfilFastener,
  type CreateProfilFastenerBody,
  type ListProfilFastenersParams,
  type PaginatedProfilFasteners,
  type ProfilFastener,
  type UpdateProfilFastenerBody,
} from "../api/profilFasteners";

export function useProfilFasteners(params: ListProfilFastenersParams) {
  return useQuery<PaginatedProfilFasteners>({
    queryKey: ["profil-fasteners", "list", params],
    queryFn: () => listProfilFasteners(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useProfilFastener(partNo: string | null) {
  return useQuery<ProfilFastener>({
    queryKey: ["profil-fasteners", "detail", partNo],
    queryFn: () => getProfilFastener(partNo as string),
    enabled: !!partNo,
    staleTime: 30_000,
  });
}

export function useCreateProfilFastener() {
  const queryClient = useQueryClient();
  return useMutation<ProfilFastener, Error, CreateProfilFastenerBody>({
    mutationFn: (body) => createProfilFastener(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["profil-fasteners"] });
    },
  });
}

export function useUpdateProfilFastener() {
  const queryClient = useQueryClient();
  return useMutation<ProfilFastener, Error, { partNo: string; body: UpdateProfilFastenerBody }>({
    mutationFn: ({ partNo, body }) => updateProfilFastener(partNo, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["profil-fasteners"] });
    },
  });
}

/** Soft-delete via DELETE /v1/profil-fasteners/:partNo. */
export function useDeleteProfilFastener() {
  const queryClient = useQueryClient();
  return useMutation<{ success: true }, Error, string>({
    mutationFn: (partNo) => deleteProfilFastener(partNo),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["profil-fasteners"] });
    },
  });
}
